<template>
  <div class="l-container--width-90">
    <v-overlay :model-value="showOverlay">
      <v-progress-circular color="background" indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <v-container fluid>
      <v-row>
        <v-col cols="12" md="4" class="text-left">
          <h1 :class="{ 'c-text--mobile-h1': $vuetify.display.mobile }">Participants</h1>
          <disclaimer />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="4" class="text-left">
          <v-row class="row--padding-top">
            <v-card class="d-flex flex-wrap" color="background" flat tile>
              <v-combobox
                class="mx-2 combobox"
                v-model="selectedAdversaries"
                :items="adversaries"
                item-title="display_name"
                item-value="name"
                label="Adversaries"
                @update:modelValue="processURLUpdate()"
                multiple
                chips
                closable-chips
                variant="solo"
                bg-color="background"
                color="primary"
              >
                <template v-slot:chip>
                  <v-chip closable color="primary"></v-chip>
                </template>
                <template v-slot:prepend-item>
                  <v-card flat width="100%" height="100%" class="c-select-dropdown"></v-card>
                </template>
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-title>
                      No adversary names matched your search.
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-combobox>
            </v-card>
          </v-row>
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            label="Participant Search"
            v-model="searchFilter"
            prepend-inner-icon="mdi-magnify"
            variant="solo"
            bg-color="background"
            hide-details
            @keydown.enter="goToCredits()"
          >
          </v-text-field>
        </v-col>
        <v-col cols="12" md="4" :class="{ 'text-right': !$vuetify.display.mobile }">
          <router-link v-if="route.params.domain !== 'trials-deceptions' && route.params.domain != 'managed-services'" :to="`/${getDomain(route.params.domain).domain}/participant-comparison/`">
            <v-btn color="primary">Compare Participants</v-btn>
          </router-link>
        </v-col>
      </v-row>
    </v-container>
    <v-container class="d-flex flex-wrap justify-center" fluid v-if="displayedParticipants.length > 0">
      <template v-for="p in participants" :key="p.name">
        <v-hover v-slot:default="{ isHovering, props}">
          <v-card
          v-bind="props"
            :style="[
              displayedParticipants.includes(p.name) ? { display: 'block' } : { display: 'none' },
            ]"
            :class="{
              'c-card--border-primary': isHovering,
              'pa-2 ma-2': !$vuetify.display.mobile,
              'pa-1 ma-1': $vuetify.display.mobile,
            }"
            elevation="2"
            outlined
            tile
            color="background"
          >
            <router-link
              :to="`/${route.params.domain}/participants/${p.name}?view=${
                getDomain(route.params.domain).participant_view
              }`"
            >
              <v-img
                v-if="$vuetify.display.mobile"
                height="70"
                width="90"
                contain
                :src="$imageSrc + p.logo"
              ></v-img>
              <v-img v-else height="180" width="220" contain :src="$imageSrc + p.logo"></v-img>
            </router-link>
          </v-card>
        </v-hover>
      </template>
    </v-container>
  </div>
</template>

<script>
import Disclaimer from './Disclaimer.vue';
import { getDomainFromURL, updateURL } from '../mixins';
import { useRoute, useRouter } from 'vue-router';

export default {
  components: { Disclaimer },
  name: 'ViewAllParticipants',
  data: () => ({
    adversaries: [],
    participants: [],
    searchFilter: '',
    selectedAdversaries: [],
    showOverlay: true,
    url_domain: '',
    trendmicro: undefined,
    route: useRoute(),
    router: useRouter(),
  }),
  watch: {
    'route.path': function () {
      if(this.route.params.domain){
         // Edge case for if user navigates between participant pages
        this.url_domain = getDomainFromURL(this.route.params.domain.toLowerCase()).domain;
        this.participants = [];
        this.selectedAdversaries = [];
        this.adversaries = [];
        this.getParticipants();
        this.getAdversaries();
      }
    },
    'route.query': function () {
      this.parseURLParams();
    },
  },
  computed: {
    displayedParticipants: function () {
       if(this.trendmicro || this.url_domain!=="MANAGED_SERVICES"){
        const selectedAdversaryNames = this.selectedAdversaries.map((adversary) => adversary.name);
        let filteredResult = this.participants.filter((participant) => {
          const participantAdversaryNames = participant.adversaries_completed.map((adv) => adv.name);
          for (const participantAdversaryName of participantAdversaryNames) {
            if (selectedAdversaryNames.includes(participantAdversaryName)){
              return true;
            }
          }
          return false;
        });
        this.url_domain === "MANAGED_SERVICES" && this.selectedAdversaries.length && filteredResult.push(this.trendmicro);
        filteredResult = filteredResult.filter(
          (participant) =>
            participant.name.includes(this.searchFilter.toLowerCase()) ||
            participant.search_tags.includes(this.searchFilter.toLowerCase()),
        );
        return filteredResult.map((participant) => participant.name);
      }
      return [];
    },
  },
  methods: {
    getDomain(url){
      return getDomainFromURL(url);
    },
    goToCredits() {
      if (this.searchFilter === 'ATT&CKEvalsCredits2022') {
        this.router.push(`/credits-${this.$appUUID}`);
      }
    },
    processURLUpdate() {
      let selectedAdversaryNames = [];

      this.selectedAdversaries.forEach((adv) => {
        selectedAdversaryNames.push(adv.name);
      });

      if (selectedAdversaryNames.length) {
        updateURL([], {
          adversaries: selectedAdversaryNames.join(','),
        });
      } else {
        updateURL(['adversaries'], {});
      }
    },
    removeAdversary(adv) {
      this.selectedAdversaries.splice(this.selectedAdversaries.indexOf(adv), 1);
      this.selectedAdversaries = [...this.selectedAdversaries];
      this.processURLUpdate();
    },
    getParticipants() {
      this.$api
        .get(`participants/?domain=${this.url_domain}`)
        .then((response) => {
          if (!response.data.length) this.$handleNotFound();

          for (const participant of response.data) {
            if (participant.name !== 'baseline-apt29-deceptions') {
              this.participants.push({
                name: `${participant.name.toLowerCase()}`,
                display_name: `${participant.display_name}`,
                logo: participant.logo_image,
                adversaries_completed: participant.adversaries_completed,
                search_tags: `${participant.search_tags}`.toLowerCase(),
              });
            }
          }
          if (this.url_domain === 'MANAGED_SERVICES'){
            this.$api
              .get(`participants/?name=trendmicro`).then(response => {
                this.trendmicro = response.data[0]
                this.trendmicro['logo'] = this.trendmicro['logo_image']
                if (!this.participants.find(participant => participant.name === 'trendmicro')) {
                  this.participants.push(this.trendmicro)
                  this.participants = this.participants.sort(function(a,b) {
                    return (a.name > b.name ? 1 : -1)
                  });
                }
              })
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },
    getAdversaries() {
      this.$api.get(`adversaries/?domain=${this.url_domain}&status=PUBLISHED`).then((response) => {
        for (const adversary of response.data) {
          this.adversaries.push({
            display_name: `${adversary.display_name}`,
            name: `${adversary.name}`,
          });
        }
        this.selectedAdversaries = [...this.adversaries];
        this.parseURLParams();
        this.processURLUpdate();
        this.showOverlay = false;
      });
    },
    parseURLParams: function () {
      let uri = window.location.search;
      const urlParams = new URLSearchParams(uri);
      let adversariesStr = urlParams.get('adversaries');
      if (adversariesStr) {
        const newSelectedAdversaries = [];
        adversariesStr.split(',').forEach((advName) => {
          this.adversaries.forEach((adv) => {
            if (advName === adv.name) newSelectedAdversaries.push(adv);
          });
        });
        this.selectedAdversaries = newSelectedAdversaries;
      }
    },
  },
  created() {
    this.url_domain = getDomainFromURL(this.route.params.domain).domain;
    this.getParticipants();
    this.getAdversaries();
  },
};
</script>

<style scoped lang="scss">
  @import "../sass/pages/_view-all-participants.scss";
</style>
